import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import {
  HR_ACTION_STATUSES,
  HR_LIFECYCLE_ACTION_TYPES,
  HrEmployeeActionRequest,
  HrLifecycleDashboard
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-lifecycle',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-lifecycle.component.html',
  styleUrls: ['./hr-lifecycle.component.scss']
})
export class HrLifecycleComponent implements OnInit {
  activeListTab = 'actions';
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  actionsLoading = false;
  dashboard: HrLifecycleDashboard = {};
  actions: HrEmployeeActionRequest[] = [];
  statusFilter = 'PENDING';
  actionTypeFilter = '';

  readonly canManageLifecycle = this.permissionService.hasPermission(app_permissions.hr.lifecycle.manage);
  readonly statusOptions = HR_ACTION_STATUSES.map(value => ({ label: this.format(value), value }));
  readonly actionTypeOptions = HR_LIFECYCLE_ACTION_TYPES.map(value => ({ label: this.format(value), value }));

  ngOnInit(): void {
    this.load();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Pending Requests', value: this.dashboard.pendingActionRequests ?? 0, tone: (this.dashboard.pendingActionRequests ?? 0) > 0 ? 'warn' : 'success' },
      { label: 'Transfer Requests', value: this.dashboard.pendingTransferRequests ?? 0, tone: 'info' },
      { label: 'Termination Requests', value: this.dashboard.pendingTerminationRequests ?? 0, tone: (this.dashboard.pendingTerminationRequests ?? 0) > 0 ? 'danger' : 'success' },
      { label: 'Compensation Placeholders', value: this.dashboard.pendingCompensationRequests ?? 0 },
      { label: 'Open Onboarding', value: this.dashboard.activeOnboardingPlans ?? 0 },
      { label: 'Open Offboarding', value: this.dashboard.activeOffboardingPlans ?? 0 },
      { label: 'Overdue Onboarding Tasks', value: this.dashboard.overdueOnboardingTasks ?? 0, tone: (this.dashboard.overdueOnboardingTasks ?? 0) > 0 ? 'warn' : 'success' },
      { label: 'Overdue Offboarding Tasks', value: this.dashboard.overdueOffboardingTasks ?? 0, tone: (this.dashboard.overdueOffboardingTasks ?? 0) > 0 ? 'warn' : 'success' }
    ];
  }

  load(): void {
    this.loading = true;
    this.hrService.lifecycleDashboard().subscribe({
      next: response => {
        this.dashboard = response.data || {};
        this.actions = this.dashboard.recentActionRequests || [];
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Lifecycle', detail: 'Unable to load lifecycle dashboard.' }),
      complete: () => this.loading = false
    });
  }

  loadActions(): void {
    this.actionsLoading = true;
    this.hrService.actionRequests(undefined, this.statusFilter, this.actionTypeFilter).subscribe({
      next: response => this.actions = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Lifecycle', detail: 'Unable to load action requests.' }),
      complete: () => this.actionsLoading = false
    });
  }

  clearFilters(): void {
    this.statusFilter = '';
    this.actionTypeFilter = '';
    this.loadActions();
  }

  approve(action: HrEmployeeActionRequest): void {
    if (!this.canManageLifecycle || !action.actionRequestId) return;
    const request = action.actionType === 'TERMINATION'
      ? this.hrService.approveTermination(action.actionRequestId)
      : this.hrService.approveAction(action.actionRequestId);
    request.subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Lifecycle', detail: 'Action request approved.' });
        this.load();
        this.loadActions();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Lifecycle', detail: 'Unable to approve action request.' })
    });
  }

  closedTasks(tasks?: { taskStatus?: string }[]): number {
    return (tasks || []).filter(task => ['COMPLETED', 'WAIVED', 'NOT_REQUIRED', 'CANCELLED'].includes(task.taskStatus || '')).length;
  }

  currentSummary(action: HrEmployeeActionRequest): string {
    return [
      action.currentDepartmentId ? `Dept ${action.currentDepartmentId}` : '',
      action.currentPositionId ? `Pos ${action.currentPositionId}` : '',
      action.currentWorkLocation || ''
    ].filter(Boolean).join(' / ') || '-';
  }

  requestedSummary(action: HrEmployeeActionRequest): string {
    if (action.actionType === 'COMPENSATION_CHANGE') {
      return action.compensationPlaceholderJson || 'Placeholder';
    }
    return [
      action.requestedDepartmentId ? `Dept ${action.requestedDepartmentId}` : '',
      action.requestedPositionId ? `Pos ${action.requestedPositionId}` : '',
      action.requestedWorkLocation || '',
      action.requestedStatus || ''
    ].filter(Boolean).join(' / ') || '-';
  }

  planSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'COMPLETED') return 'success';
    if (status === 'IN_PROGRESS') return 'warn';
    if (status === 'OPEN') return 'info';
    if (status === 'CANCELLED') return 'danger';
    return 'secondary';
  }

  actionSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'APPROVED') return 'success';
    if (status === 'PENDING') return 'warn';
    if (status === 'REJECTED' || status === 'CANCELLED') return 'danger';
    return 'secondary';
  }

  format(value?: string): string {
    return (value || '-').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }
}
